import { ReactNode, useEffect, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

interface AdminGateProps {
  children: ReactNode;
}

const AdminGate = ({ children }: AdminGateProps) => {
  const { slug: urlSlug } = useParams<{ slug: string }>();
  const { adminSlug, adminSlugLoading, adminSlugError, getAdminSlug } = useAuth();
  const [resolvedSlug, setResolvedSlug] = useState<string | null>(adminSlug ?? null);
  const [loadingSlug, setLoadingSlug] = useState(adminSlugLoading);
  const [error, setError] = useState<string | null>(adminSlugError);

  useEffect(() => {
    let active = true;

    const ensureSlug = async () => {
      if (adminSlug) {
        setResolvedSlug(adminSlug);
        setLoadingSlug(false);
        return;
      }
      setLoadingSlug(true);
      try {
        const slug = await getAdminSlug();
        if (!active) return;
        setResolvedSlug(slug);
        setError(null);
      } catch (err) {
        if (!active) return;
        const message = (err as Error)?.message ?? "Não foi possível carregar o slug.";
        setError(message);
      } finally {
        if (active) setLoadingSlug(false);
      }
    };

    ensureSlug();

    return () => {
      active = false;
    };
  }, [adminSlug, getAdminSlug]);

  if (loadingSlug) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-sm text-muted-foreground">Carregando espaço admin...</p>
      </div>
    );
  }

  if (error || !resolvedSlug) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center space-y-2">
          <p className="text-sm font-medium text-destructive">Erro ao carregar slug do admin.</p>
          <p className="text-xs text-muted-foreground">{error ?? "Tente novamente mais tarde."}</p>
        </div>
      </div>
    );
  }

  if (urlSlug !== resolvedSlug) {
    return <Navigate to={`/admin/${resolvedSlug}`} replace />;
  }

  return <>{children}</>;
};

export default AdminGate;

